import { IViolation } from 'app/model/base/annotation';
import { regions, dataFlowSupported, formatRegionString } from 'app/shared/utils/region.util';
import {
    AzureBlobFSLinkedService,
    AzureBlobStorageLinkedService,
    IntegrationRuntime,
    ManagedIntegrationRuntime,
    Metamodel,
    SelfHostedIntegrationRuntime,
    azureBlobFSAuthTypes,
    azureStorageAuthTypes,
    LinkedService,
    irTypesConst,
    IValidationContext
} from 'shell-models-lib';

import { ExecuteWranglingDataflowActivity } from './execute-wrangling-dataflow.activity';
import { ExecuteDataFlowActivity } from './execute-dataflow.activity';
import { StorageAccountKind } from 'app/apis/azure-storage-constants';
import { servicePrincipalCredentialTypes } from '../linkedservice/dynamics.linkedservice';
import { sanitizeAndFormatString, stringIsNullOrEmpty } from 'app/shared/utils/string.util';
import { isSapOdpSource } from 'app/model/dataflow/dataflow-utils';
import { FactoryUrlInformationService } from 'app/services/factory-url-information.service';
import { compact } from 'lodash';
import { getServiceInstance } from 'app/multi-contexts/modules/root/root.application';
import { ExposureControlService } from 'app/services/exposure-control/base-exposure-control.service';

export async function validateIRConstraints(
    dataflowActivity: ExecuteDataFlowActivity | ExecuteWranglingDataflowActivity,
    integrationRuntime: IntegrationRuntime,
    violation: IViolation,
    context: IValidationContext
): Promise<IViolation[]> {
    const violations: IViolation[] = [];
    const dataFlowSupportedRegion =
        !integrationRuntime ||
        (integrationRuntime instanceof ManagedIntegrationRuntime &&
            (integrationRuntime.computeLocation === regions.AutoResolve ||
                dataFlowSupported(integrationRuntime.computeLocation, { exposureControlService: await getServiceInstance<ExposureControlService>(
                    context.contextData,
                    'ExposureControlService'
                ) })));
    if (!dataFlowSupportedRegion) {
        violations.push({
            context: dataflowActivity,
            message: () => stringResources.DataFlow_No_AutoResolve_IntegrationRuntime_Error
        });
    } else if (
        __IS_A365__ &&
        dataflowActivity &&
        integrationRuntime &&
        (!(integrationRuntime instanceof ManagedIntegrationRuntime) ||
            integrationRuntime.computeLocation !== regions.AutoResolve)
    ) {
        violations.push({
            context: dataflowActivity,
            message: () => stringResources.DataFlow_No_AutoResolve_IntegrationRuntime_Error
        });
    }

    /**
     * If Data Flow contains Linked services that use self-hosted Integration runtime, the Data flow Integration runtime
     * must be auto-resolved or in the same region as Data Factory.
     */
    if (Metamodel.modelOf(dataflowActivity).name === 'ExecuteDataFlow') {
        const executeDataFlowActivity: ExecuteDataFlowActivity = dataflowActivity as ExecuteDataFlowActivity;
        if (executeDataFlowActivity.dataflow) {
            const linkedServices: LinkedService[] = compact([
                ...executeDataFlowActivity.dataflow
                    .getSourceTransformations()
                    .map((source) =>
                        source.isInlineDataset ? source.sourceLinkedService : source.sourceDataset?.linkedService
                    ),
                ...executeDataFlowActivity.dataflow
                    .getSinkTransformations()
                    .map((sink) =>
                        sink.isInlineDataset ? sink.sinkLinkedService : sink.sinkDataset?.linkedService
                    )
            ]);
            const hasSelfHostedIR = linkedServices.some(
                (linkedService) => Metamodel.modelOf(linkedService.integrationRuntime)?.name === irTypesConst.SelfHosted
            );
            if (hasSelfHostedIR && Metamodel.modelOf(integrationRuntime)?.name === irTypesConst.Managed) {
                const integrationRuntimeRegion = formatRegionString(
                    (integrationRuntime as ManagedIntegrationRuntime).computeLocation
                );
                const factoryUrlInformationService = (await getServiceInstance<FactoryUrlInformationService>(context.contextData, 'FactoryUrlInformationService'));
                const dataFactoryRegion = formatRegionString(factoryUrlInformationService.dataFactoryLocation);
                if (
                    integrationRuntimeRegion !== regions.AutoResolve &&
                    (!stringIsNullOrEmpty(dataFactoryRegion) && integrationRuntimeRegion !== dataFactoryRegion)
                ) {
                    violations.push({
                        context: executeDataFlowActivity,
                        message: () => stringResources.DataFlow_IntegrationRuntime_NotSameRegion_Error
                    });
                }
            }
        }
    }
    return violations;
}

export function validateStagingLinkedService(dataflowActivity: ExecuteDataFlowActivity | ExecuteWranglingDataflowActivity, stagingRequired: boolean, attributeName: string): IViolation[] {
    if (stagingRequired) {
        if (!dataflowActivity.stagingLinkedService) {
            return [{
                context: dataflowActivity,
                message: () => sanitizeAndFormatString(stringResources.RequiredValidatorMessage, Metamodel.modelOf(dataflowActivity).display[0],
                    dataflowActivity.name ? '\'' + dataflowActivity.name + '\'' : '', attributeName)
            }];
        } else if (dataflowActivity.stagingLinkedService instanceof AzureBlobStorageLinkedService &&
            (dataflowActivity.stagingLinkedService.authenticationMethod === azureStorageAuthTypes.MSIAuthentication
                || dataflowActivity.stagingLinkedService.authenticationMethod === azureStorageAuthTypes.ServicePrincipalAuthentication)) {
            if (dataflowActivity.stagingLinkedService.accountKind !== StorageAccountKind.GeneralPurposeV2
                && dataflowActivity.stagingLinkedService.accountKind !== StorageAccountKind.BlobStorage
                && dataflowActivity.stagingLinkedService.accountKind !== StorageAccountKind.BlockBlobStorage) {
                return [{
                    context: dataflowActivity.stagingLinkedService,
                    message: () => stringResources.Dataflow_StagingLinkedService_BlobAccountKindError
                }];
            }
        } else if (dataflowActivity.stagingLinkedService instanceof AzureBlobFSLinkedService
            && dataflowActivity.stagingLinkedService.authenticationMethod === azureBlobFSAuthTypes.ServicePrincipalAuthentication
            && dataflowActivity.stagingLinkedService.servicePrincipalCredentialType === servicePrincipalCredentialTypes.ServicePrincipalCert) {
            return [{
                context: dataflowActivity.stagingLinkedService,
                message: () => stringResources.Dataflow_StagingLinkedService_Gen2AuthTypeError
            }];
        } else if ((dataflowActivity.stagingLinkedService instanceof AzureBlobStorageLinkedService ||
            dataflowActivity.stagingLinkedService instanceof AzureBlobFSLinkedService)
            && dataflowActivity.stagingLinkedService.authenticationMethod === azureStorageAuthTypes.SasUriAuthentication) {
            return [{
                context: dataflowActivity.stagingLinkedService,
                message: () => stringResources.Dataflow_StagingLinkedService_BlobSASError
            }];
        }

        /**
         * Do not use `AzureBlobStorageLinkedService` or `AzureBlobFSLinkedService` with Self-host IR as SAP CDC
         * dataflow's staging linked service.
         */
        if (
            (dataflowActivity.stagingLinkedService instanceof AzureBlobStorageLinkedService ||
                dataflowActivity.stagingLinkedService instanceof AzureBlobFSLinkedService) &&
            dataflowActivity.stagingLinkedService.integrationRuntime instanceof SelfHostedIntegrationRuntime
        ) {
            const dataflow = dataflowActivity instanceof ExecuteDataFlowActivity
                ? dataflowActivity.dataflow
                : dataflowActivity.wranglingDataflow;
            if (Array.from(dataflow.sources).some((source) => isSapOdpSource(source))) {
                return [
                    {
                        context: dataflowActivity.stagingLinkedService,
                        message: () =>
                            sanitizeAndFormatString(
                                stringResources.DataFlow_SAPCDC_StagingLinkedService_Error,
                                Metamodel.modelOf(dataflowActivity.stagingLinkedService).display[0]
                            )
                    }
                ];
            }
        }
    } else if (dataflowActivity.stagingLinkedService) {
        return [{
            context: dataflowActivity.stagingLinkedService,
            message: () => stringResources.NewDataflow_StagingLinkedService_NotNeeded
        }];
    }
    return [];
}
